import React, { useEffect, useMemo, useState } from 'react';
import PageHeader from '../components/PageHeader';
import Card, { CardHeader } from '../components/Card';
import StatCard from '../components/StatCard';
import SearchInput from '../components/SearchInput';
import DataTable, { Column } from '../components/DataTable';
import Badge from '../components/Badge';
import Pagination from '../components/Pagination';
import axios from 'axios';
import { BaseUrl, withFallback } from '../services/adminApi';
import { faArrowsRotate, faIndianRupeeSign, faUserPlus, faUserXmark } from '@fortawesome/free-solid-svg-icons';

interface ReportRow {
  id: string | number;
  result_id: number;
  check_type: 'plagiarism' | 'ai';
  document: string;
  score: number | null;
  status: 'complete' | 'failed' | 'processing' | string;
  submitted_at: string;
  submitted_by: string;
  submitted_by_email: string;
}

interface ReportsResponse {
  results: ReportRow[];
  pagination: {
    pagenum: number;
    pagesize: number;
    total_count: number;
    total_pages: number;
  };
}

const FALLBACK_RESPONSE: ReportsResponse = {
  results: [
    { id: 1, result_id: 1, check_type: 'plagiarism', document: 'Effects of Climate Change on Coastal Cities.pdf', score: 4.2, status: 'complete', submitted_at: '30 Jun 2026', submitted_by: 'Ritika Sharma', submitted_by_email: 'ritika@example.com' },
    { id: 2, result_id: 2, check_type: 'ai', document: 'Machine Learning in Healthcare.docx', score: 62, status: 'complete', submitted_at: '29 Jun 2026', submitted_by: 'Arjun Mehta', submitted_by_email: 'arjun@example.com' },
    { id: 3, result_id: 3, check_type: 'plagiarism', document: 'Quarterly Market Analysis Report.pdf', score: 18.7, status: 'complete', submitted_at: '28 Jun 2026', submitted_by: 'Global Press Ltd', submitted_by_email: 'contact@globalpress.example' },
    { id: 4, result_id: 4, check_type: 'ai', document: 'Renewable Energy Policy Review.docx', score: 12, status: 'complete', submitted_at: '27 Jun 2026', submitted_by: 'Priya Nair', submitted_by_email: 'priya@example.com' },
    { id: 5, result_id: 5, check_type: 'plagiarism', document: 'Genomics Research Summary.pdf', score: 2.1, status: 'complete', submitted_at: '25 Jun 2026', submitted_by: 'Delta University', submitted_by_email: 'admin@delta.example' },
  ],
  pagination: { pagenum: 1, pagesize: 10, total_count: 5, total_pages: 1 },
};

interface SubReportRow {
  id: string;
  period_label: string;
  new_subscriptions: number;
  renewals: number;
  lapsed: number;
  revenue: number;
}

type ReportPeriod = 'daily' | 'weekly' | 'monthly';

const REPORT_TABS = ['checks', 'subscriptions'] as const;
const PERIOD_TABS: { key: ReportPeriod; label: string }[] = [
  { key: 'daily', label: 'Daily' },
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
];
const PAGE_SIZE = 10;

function scoreTone(row: ReportRow): 'green' | 'amber' | 'red' | 'gray' {
  if (row.status === 'failed' || row.score === null) return 'gray';
  const threshold = row.check_type === 'plagiarism' ? [8, 15] : [30, 60];
  if (row.score > threshold[1]) return 'red';
  if (row.score > threshold[0]) return 'amber';
  return 'green';
}

export default function Reports() {
  const [reportTab, /* setReportTab */] = useState<(typeof REPORT_TABS)[number]>('checks');

  const [reports, setReports] = useState<ReportRow[]>([]);
const [pagination, setPagination] = useState({
  pagenum: 1,
  pagesize: PAGE_SIZE,
  total_count: 0,
  total_pages: 1,
});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);

  const [subPeriod, setSubPeriod] = useState<ReportPeriod>('daily');
  const [subReport, setSubReport] = useState<SubReportRow[]>([]);
  const [subLoading, setSubLoading] = useState(false);

useEffect(() => {
  if (reportTab !== 'subscriptions') return;
  let cancelled = false;
  async function load() {
    try {
      setSubLoading(true);

      const response :any= await axios.get<ReportsResponse>(
        BaseUrl + "admin/reports/checks/",
        {
          params: {
            search: debouncedSearch || "",
            checktype: typeFilter === "all" ? "" : typeFilter,
            pagenum: page,
            pagesize: PAGE_SIZE,
          },
        }
      );
      if (cancelled) return;
      setSubReport(response.data.results ?? []);
    } finally {
      if (!cancelled) {
        setSubLoading(false);
      }
    }
  }
  load();
  return () => {
    cancelled = true;
  };
}, [reportTab, subPeriod]);

  const subTotals = useMemo(() => {
    return subReport.reduce(
      (acc, r) => ({
        new_subscriptions: acc.new_subscriptions + r.new_subscriptions,
        renewals: acc.renewals + r.renewals,
        lapsed: acc.lapsed + r.lapsed,
        revenue: acc.revenue + r.revenue,
      }),
      { new_subscriptions: 0, renewals: 0, lapsed: 0, revenue: 0 }
    );
  }, [subReport]);

  const subColumns: Column<SubReportRow>[] = [
    { key: 'period_label', header: 'Period', render: r => <span className="font-bold text-gray-900">{r.period_label}</span> },
    { key: 'new_subscriptions', header: 'New Subscriptions', render: r => r.new_subscriptions, align: 'right' },
    { key: 'renewals', header: 'Renewals', render: r => r.renewals, align: 'right' },
    {
      key: 'lapsed', header: 'Lapsed', render: r => (
        <Badge tone={r.lapsed > 0 ? 'red' : 'green'}>{r.lapsed}</Badge>
      ), align: 'right'
    },
    { key: 'revenue', header: 'Revenue', render: r => `₹${r.revenue.toLocaleString('en-IN')}`, align: 'right' },
  ];

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      const data = await withFallback(
        axios.get<ReportsResponse>(BaseUrl + 'admin/reports/checks/', {
          params: {
            search: debouncedSearch || '',
            checktype: typeFilter === 'all' ? '' : typeFilter,
            pagenum: page,
            pagesize: PAGE_SIZE,
          },
        }),
        FALLBACK_RESPONSE
      );

      if (cancelled) return;

const resolved = data;
      const withIds = resolved.results.map(r => ({
        ...r,
        id: `${r.result_id}-${r.check_type}`,
      }));

      setReports(withIds);
      setPagination(resolved.pagination);
      setLoading(false);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, typeFilter, page]);

  const columns: Column<ReportRow>[] = useMemo(
    () => [
      { key: 'document', header: 'Document', render: r => <span className="font-bold text-gray-900">{r.document}</span> },
      {
        key: 'submitted_by',
        header: 'Submitted by',
        render: r => (
          <div className="flex flex-col">
            <span>{r.submitted_by}</span>
            <span className="text-[12px] text-gray-400">{r.submitted_by_email}</span>
          </div>
        ),
      },
      {
        key: 'check_type',
        header: 'Check type',
        render: r => (
          <Badge tone={r.check_type === 'plagiarism' ? 'blue' : 'purple'} dot={false}>
            {r.check_type === 'plagiarism' ? 'Plagiarism' : 'AI Detection'}
          </Badge>
        ),
      },
      {
        key: 'score',
        header: 'Score',
        render: r =>
          r.status === 'failed' || r.score === null ? (
            <Badge tone="gray">Failed</Badge>
          ) : (
            <Badge tone={scoreTone(r)}>{r.score}%</Badge>
          ),
      },
      { key: 'submitted_at', header: 'Submitted', render: r => r.submitted_at, align: 'right' },
    ],
    []
  );

  return (
    <div>
      <PageHeader
        title="Reports"
        subtitle="Plagiarism/AI-detection checks and subscription activity across the platform."
      />

      {/* <div className="flex gap-2 mb-5">
        {REPORT_TABS.map(t => (
          <button
            key={t}
            onClick={() => setReportTab(t)}
            className={`text-[13px] font-bold px-4 py-2 rounded-lg transition-colors capitalize ${reportTab === t ? 'bg-navy text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
          >
            {t === 'checks' ? 'Reports & Checks' : 'Subscription Reports'}
          </button>
        ))}
      </div> */}

      {reportTab === 'checks' ? (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <SearchInput
              value={search}
              onChange={v => setSearch(v)}
              placeholder="Search by document or user…"
              className="w-full sm:w-64"
            />
            <select
              value={typeFilter}
              onChange={e => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
              className="text-[13px] font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-navy"
            >
              <option value="all">All check types</option>
              <option value="plagiarism">Plagiarism</option>
              <option value="ai">AI Detection</option>
            </select>
          </div>

          <DataTable
            columns={columns}
            rows={reports}
            loading={loading}
            emptyTitle="No reports found"
            emptySubtitle="Try a different search term or filter."
          />
          <Pagination
            page={pagination.pagenum}
            totalPages={pagination.total_pages}
            onChange={setPage}
            totalItems={pagination.total_count}
            pageSize={pagination.pagesize}
          />
        </Card>
      ) : (
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-5">
            <StatCard label="New Subscriptions" value={subTotals.new_subscriptions} icon={faUserPlus} iconBg="#eff6ff" iconColor="#2563eb" />
            <StatCard label="Renewals" value={subTotals.renewals} icon={faArrowsRotate} iconBg="#f0fdf4" iconColor="#16a34a" />
            <StatCard label="Lapsed" value={subTotals.lapsed} icon={faUserXmark} iconBg="#fef2f2" iconColor="#dc2626" />
            <StatCard label="Revenue" value={`₹${subTotals.revenue.toLocaleString('en-IN')}`} icon={faIndianRupeeSign} iconBg="#f5f3ff" iconColor="#7c3aed" />
          </div>

          <Card>
            <CardHeader
              title="Subscription report"
              subtitle="New sign-ups, renewals, lapsed subscribers and revenue by period"
              action={
                <div className="flex gap-1.5">
                  {PERIOD_TABS.map(p => (
                    <button
                      key={p.key}
                      onClick={() => setSubPeriod(p.key)}
                      className={`text-[12px] font-bold px-3 py-1.5 rounded-lg transition-colors ${subPeriod === p.key ? 'bg-navy text-white' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                        }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              }
            />
            <DataTable
              columns={subColumns}
              rows={subReport.map(r => ({ ...r, id: r.id }))}
              loading={subLoading}
              emptyTitle="No data for this period"
              emptySubtitle="Try a different period."
            />
          </Card>

          <Card className="mt-5">
            <CardHeader title="More reports for Manna Plag" subtitle="Suggested — let us know which one to build next" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { icon: 'ti-chart-donut', title: 'Plan-wise revenue split', desc: 'Revenue and subscriber count broken down by plan (Starter, Professional, Institution, Publisher Pro).' },
                { icon: 'ti-trending-down', title: 'Churn / lapsed trend', desc: 'Month-over-month lapsed-subscriber rate, tied in with the new Lapsed page.' },
                { icon: 'ti-file-search', title: 'Plagiarism vs AI-check volume', desc: 'Daily/weekly checks submitted, split by check type, with average score trend.' },
                { icon: 'ti-users', title: 'Top submitters', desc: 'Users or institutions submitting the most checks in a given period.' },
                { icon: 'ti-headset', title: 'Ticket resolution report', desc: 'Support ticket volume, average resolution time, and open vs resolved by day/week.' },
                { icon: 'ti-api', title: 'API usage report', desc: 'API call volume per key/customer — useful for institution & publisher plans.' },
              ].map(item => (
                <div key={item.title} className="flex items-start gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50/60">
                  <div className="w-9 h-9 rounded-lg bg-white border border-gray-100 flex items-center justify-center flex-shrink-0">
                    <i className={`ti ${item.icon} text-navy`} style={{ fontSize: 17 }} />
                  </div>
                  <div>
                    <p className="text-[13px] font-black text-gray-900">{item.title}</p>
                    <p className="text-[12px] text-gray-500 font-medium mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}